import { create } from 'zustand';
import type { CatalogProduct } from '@features/catalog/types';
import type { Holding } from '@features/holdings/types';

type AvcsMode = 'products' | 'cells';

export type EditAction =
  | {
      type: 'addVertex';
      afterWaypointIndex: number;
      lat: number;
      lon: number;
    }
  | { type: 'deleteVertex'; waypointIndex: number };

export interface EditWaypoint {
  index: number;
  lat: number;
  lon: number;
  name?: string;
}

interface MapStore {
  /** AVCS layer mode toggle: show product polygons or cell boundaries. */
  avcsMode: AvcsMode;
  /** Show/hide port markers on map. */
  portsVisible: boolean;
  /** Route editing mode active (allows waypoint drag/drop). */
  routeEditMode: boolean;
  /** Up to 2 route IDs visible as overlays (eye icon toggles). */
  visibleRouteOverlays: string[];
  /** Currently selected route (highlighted on map). */
  selectedRouteId: string | null;
  /** Route ID for which products are currently displayed on map. */
  activeProductsRouteId: string | null;
  /** Currently selected product ID (for selection sync between map and list). */
  selectedProductId: string | null;
  /** Product ID currently under the mouse cursor on the map. */
  hoveredProductId: string | null;
  /** True once another product is hovered while one is selected — switches selected style from info to status-based. */
  selectedDeemphasized: boolean;
  /** IDs of products that pass current filters (to show only filtered products on map). */
  filteredProductIds: string[] | null;
  /** Catalog products to display on the map (set by CatalogPage, cleared on unmount). */
  catalogProducts: CatalogProduct[] | null;
  /** Product ID to fit the map view to (consumed and cleared by map hook). */
  fitProductId: string | null;
  /** Waypoints being edited (set by useRouteEdit, consumed by useRouteEditSync). */
  editWaypoints: EditWaypoint[] | null;
  /** Whether the basket page is active (basket products should render on map). */
  basketProductsActive: boolean;
  /** Holdings to display on the map (set by HoldingsPage, cleared on unmount). */
  holdingsProducts: Holding[] | null;
  /** Pending edit action from map interactions, consumed by useRouteEdit. */
  pendingEditAction: EditAction | null;
  /** Coordinate to pan the map to (consumed and cleared by map hook). */
  panToCoordinate: { lon: number; lat: number; zoomIn?: boolean } | null;
  /** Port ID selected as departure in route creation (highlighted on map). */
  departurePortId: number | null;
  /** Port ID selected as arrival in route creation (highlighted on map). */
  arrivalPortId: number | null;

  toggleAvcsMode: () => void;
  togglePorts: () => void;
  setRouteEditMode: (enabled: boolean) => void;
  setVisibleRouteOverlays: (ids: string[]) => void;
  setSelectedRouteId: (id: string | null) => void;
  setActiveProductsRouteId: (id: string | null) => void;
  setSelectedProductId: (id: string | null) => void;
  setHoveredProductId: (id: string | null) => void;
  setFilteredProductIds: (ids: string[] | null) => void;
  setCatalogProducts: (products: CatalogProduct[] | null) => void;
  setFitProductId: (id: string | null) => void;
  setEditWaypoints: (waypoints: EditWaypoint[] | null) => void;
  setBasketProductsActive: (active: boolean) => void;
  setHoldingsProducts: (holdings: Holding[] | null) => void;
  setPendingEditAction: (action: EditAction | null) => void;
  setPanToCoordinate: (coord: { lon: number; lat: number; zoomIn?: boolean } | null) => void;
  setDeparturePortId: (id: number | null) => void;
  setArrivalPortId: (id: number | null) => void;
}

/**
 * UI-only store for map layer state and route overlays.
 * No API data stored here — use React Query for route data.
 */
export const useMapStore = create<MapStore>((set) => ({
  avcsMode: 'products',
  portsVisible: true,
  routeEditMode: false,
  visibleRouteOverlays: [],
  selectedRouteId: null,
  activeProductsRouteId: null,
  selectedProductId: null,
  hoveredProductId: null,
  selectedDeemphasized: false,
  filteredProductIds: null,
  catalogProducts: null,
  fitProductId: null,
  editWaypoints: null,
  basketProductsActive: false,
  holdingsProducts: null,
  pendingEditAction: null,
  panToCoordinate: null,
  departurePortId: null,
  arrivalPortId: null,

  toggleAvcsMode: () =>
    set((state) => ({ avcsMode: state.avcsMode === 'products' ? 'cells' : 'products' })),

  togglePorts: () => set((state) => ({ portsVisible: !state.portsVisible })),

  setRouteEditMode: (enabled) => set({ routeEditMode: enabled }),

  setVisibleRouteOverlays: (ids) => {
    // Enforce max 2 overlays
    const capped = ids.slice(0, 2);
    set({ visibleRouteOverlays: capped });
  },

  setSelectedRouteId: (id) => set({ selectedRouteId: id }),

  setActiveProductsRouteId: (id) => set({ activeProductsRouteId: id }),

  setSelectedProductId: (id) =>
    set({ selectedProductId: id, selectedDeemphasized: false, hoveredProductId: null }),

  setHoveredProductId: (id) =>
    set((state) => {
      const deemphasized =
        state.selectedDeemphasized ||
        (id !== null && state.selectedProductId !== null && id !== state.selectedProductId);
      return { hoveredProductId: id, selectedDeemphasized: deemphasized };
    }),

  setFilteredProductIds: (ids) => set({ filteredProductIds: ids }),

  setCatalogProducts: (products) => set({ catalogProducts: products }),

  setFitProductId: (id) => set({ fitProductId: id }),

  setEditWaypoints: (waypoints) => set({ editWaypoints: waypoints }),

  setBasketProductsActive: (active) => set({ basketProductsActive: active }),

  setHoldingsProducts: (holdings) => set({ holdingsProducts: holdings }),

  setPendingEditAction: (action) => set({ pendingEditAction: action }),

  setPanToCoordinate: (coord) => set({ panToCoordinate: coord }),

  setDeparturePortId: (id) => set({ departurePortId: id }),

  setArrivalPortId: (id) => set({ arrivalPortId: id }),
}));
